import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  CircularProgress,
  LinearProgress,
  Paper
} from '@mui/material';
import { GENERATE_NAME_ENDPOINT } from '../config/api';

const languages = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'fr', name: 'Français' },
  { code: 'de', name: 'Deutsch' },
  { code: 'ru', name: 'Русский' },
  { code: 'ar', name: 'العربية' },
  { code: 'ko', name: '한국어' },
  { code: 'ja', name: '日本語' },
  { code: 'zh', name: '中文' }
];

function HomePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [name, setName] = useState('');
  const [generationState, setGenerationState] = useState({
    status: 'idle',  // idle, generating, complete, error
    progress: 0,
    currentContent: '',
    result: null
  });
  const [error, setError] = useState(null);
  const [randomMode, setRandomMode] = useState(false);

  const handleLanguageSelect = (langCode) => {
    i18n.changeLanguage(langCode);
  };

  const handleGenerate = async (isRandom = false) => {
    if (!isRandom && !name.trim()) {
      setError(t('pleaseEnterName'));
      return;
    }
    
    console.log('Starting name generation - isRandom:', isRandom);
    setIsLoading(true);
    setGenerationState({
      status: 'generating',
      progress: 0,
      currentContent: '',
      result: null
    });
    setError(null);
    setRandomMode(isRandom);

    try {
      const requestBody = {
        name: isRandom ? null : name,
        language: i18n.language
      };
      console.log('Sending request with body:', requestBody);

      const response = await fetch(GENERATE_NAME_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      console.log('Response status:', response.status);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let hasNavigated = false;

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          console.log('Stream completed');
          break;
        }

        const chunk = decoder.decode(value);
        console.log('Received chunk:', chunk);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              console.log('Parsed SSE data:', data);
              
              if (data.error) {
                console.error('Received error:', data.error);
                throw new Error(data.error);
              }
              
              // 更新生成状态
              setGenerationState(prevState => ({
                ...prevState,
                status: data.status,
                progress: data.progress,
                currentContent: data.current_content,
                result: data.result
              }));

              // 当收到中文名、拼音和分值后立即跳转
              if (!hasNavigated && data.result && 
                  data.result.chineseName && 
                  data.result.pinyin && 
                  data.result.score) {
                console.log('Received initial name data with score, navigating...');
                hasNavigated = true;
                const { chineseName, pinyin, score } = data.result;
                navigate('/result', { 
                  state: {
                    chineseName,
                    pinyin,
                    score,
                    meaning: null,
                    explanation: null,
                    originalName: isRandom ? null : name
                  }
                });
              }

              // 继续接收并更新其他内容
              if (hasNavigated && data.result) {
                window.dispatchEvent(new CustomEvent('nameDataUpdate', {
                  detail: {
                    ...data.result,
                    originalName: isRandom ? null : name
                  }
                }));
              }
            } catch (e) {
              console.error('Error parsing SSE message:', e, 'Raw line:', line);
              if (!(e instanceof SyntaxError)) {
                throw e;
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Error in handleGenerate:', error);
      setGenerationState(prevState => ({
        ...prevState,
        status: 'error'
      }));
      setError(error.message);
    } finally {
      console.log('Generation process completed');
      setIsLoading(false);
    }
  };

  const getButtonText = (isRandomButton = false) => {
    if (!isLoading) {
      return t(isRandomButton ? 'random' : 'generate');
    }
    return t('generating');
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      position: 'relative',
      pb: 8 
    }}>
      {/* 顶部通知栏 */}
      <Box sx={{
        width: '100%',
        bgcolor: '#FFF',
        py: 1.5,
        textAlign: 'center',
        borderBottom: '1px solid #FFE4E8',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1
      }}>
        <Typography variant="body2" sx={{ 
          color: 'text.secondary',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5
        }}>
          {t('contact')}
        </Typography>
        <Button
          size="small"
          startIcon={<span role="img" aria-label="sweet potato">🍠</span>}
          sx={{
            color: '#FF2442',
            fontSize: '0.875rem',
            fontWeight: 600,
            backgroundColor: '#FFF5F7',
            borderRadius: '16px',
            padding: '4px 12px',
            minWidth: 'auto',
            '&:hover': {
              backgroundColor: '#FFE4E8',
            }
          }}
          onClick={() => window.open('https://www.xiaohongshu.com/user/profile/566a444ccb35fb35e85b129a?xsec_token=YBXbFDTrdsLoLLIcwMq6Z7kv-DxH8-ErAriLOKhfTt2Hg%3D&xsec_source=app_share&xhsshare=CopyLink&appuid=566a444ccb35fb35e85b129a&apptime=1737155507&share_id=9b5c59fb1c034e4cb9500c2a4d3b36f2', '_blank')}
        >
          {t('xiaohongshu')}
        </Button>
      </Box>

      <Container maxWidth="sm" sx={{ 
        pt: { xs: 8, sm: 10 },  
        px: 2
      }}>
        <Box sx={{ 
          textAlign: 'center', 
          mb: 6,
          mt: { xs: 4, sm: 6 }  
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
            <Typography variant="h1" sx={{ 
              fontSize: { xs: '2rem', sm: '2.5rem' },
              fontWeight: 'bold',
              color: '#FF2442',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              🏮 {t('title')} ✨
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ 
            color: 'text.secondary',
            mt: 2,
            lineHeight: 1.6
          }}>
            {t('subtitle')}
          </Typography>
        </Box>

        <Box sx={{ px: 2 }}>
          <TextField
            fullWidth
            variant="outlined"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('inputPlaceholder')}
            disabled={isLoading}
            sx={{ mb: 2 }}
          />

          {error && (
            <Typography 
              color="error" 
              align="center" 
              sx={{ mt: 2 }}
            >
              {error}
            </Typography>
          )}

          <Box sx={{ position: 'relative', width: '100%', mb: 2 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => handleGenerate(false)}
              disabled={isLoading || !name.trim()}
              sx={{
                height: 48,
                backgroundColor: '#FF2442',
                '&:hover': {
                  backgroundColor: '#FF1F1F'
                },
                '&.Mui-disabled': {
                  backgroundColor: '#FFE4E8',
                  color: '#FF2442'
                }
              }}
            >
              {t('generate')}
            </Button>
            {isLoading && !randomMode && (
              <LinearProgress
                variant="determinate"
                value={generationState.progress}
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  borderBottomLeftRadius: 4,
                  borderBottomRightRadius: 4,
                  backgroundColor: '#FFE4E8',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#FF2442'
                  }
                }}
              />
            )}
          </Box>

          <Box sx={{ position: 'relative', width: '100%' }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => handleGenerate(true)}
              disabled={isLoading}
              sx={{
                height: 48,
                borderColor: '#FF2442',
                color: '#FF2442',
                '&:hover': {
                  borderColor: '#FF1F1F',
                  backgroundColor: 'rgba(255, 36, 66, 0.04)'
                },
                '&.Mui-disabled': {
                  borderColor: '#FFE4E8',
                  color: '#FF2442'
                }
              }}
            >
              {t('random')}
            </Button>
            {isLoading && randomMode && (
              <LinearProgress
                variant="determinate"
                value={generationState.progress}
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  borderBottomLeftRadius: 4,
                  borderBottomRightRadius: 4,
                  backgroundColor: '#FFE4E8',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#FF2442'
                  }
                }}
              />
            )}
          </Box>
        </Box>
      </Container>

      {/* 底部语言选择器 */}
      <Box sx={{ 
        position: 'fixed', 
        bottom: 20, 
        left: '50%',
        transform: 'translateX(-50%)',
        width: '90%',
        maxWidth: '600px',
        bgcolor: '#FFF',
        borderRadius: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        p: '8px 12px',
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: '30px',
          background: 'linear-gradient(to right, rgba(255,255,255,1), rgba(255,255,255,0))',
          zIndex: 1,
          pointerEvents: 'none',
          borderTopLeftRadius: '20px',
          borderBottomLeftRadius: '20px',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: '30px',
          background: 'linear-gradient(to left, rgba(255,255,255,1), rgba(255,255,255,0))',
          zIndex: 1,
          pointerEvents: 'none',
          borderTopRightRadius: '20px',
          borderBottomRightRadius: '20px',
        }
      }}>
        <Box sx={{
          display: 'flex',
          overflowX: 'auto',
          scrollBehavior: 'smooth',
          WebkitOverflowScrolling: 'touch',
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none'
          },
          gap: '8px',
          py: '4px',
          px: '4px'
        }}>
          {languages.map((lang) => (
            <Button
              key={lang.code}
              onClick={() => handleLanguageSelect(lang.code)}
              sx={{
                fontSize: '0.875rem',
                minWidth: '80px',
                flexShrink: 0,
                bgcolor: i18n.language === lang.code ? '#FF2442' : '#F5F5F5',
                color: i18n.language === lang.code ? '#FFF' : '#333',
                '&:hover': {
                  bgcolor: i18n.language === lang.code ? '#FF2442' : '#EAEAEA',
                },
                borderRadius: '12px',
                px: '16px',
                py: '6px',
                textTransform: 'none'
              }}
            >
              {lang.name}
            </Button>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

export default HomePage;
